var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import * as Sentry from '@sentry/react';
import ReactGA from 'react-ga';
import amplitudeEvent from '../amplitude/amplitudeEvent';
var logDomWithCSS = function (dom, args, css) {
    var messages = args.reduce(function (acc, cur) {
        if (typeof cur === 'object' || Array.isArray(cur)) {
            return "".concat(acc, " ").concat(JSON.stringify(cur));
        }
        return "".concat(acc, " ").concat(cur);
    }, '');
    var logDom = document.createElement('p');
    logDom.setAttribute('style', css);
    logDom.innerText = messages;
    dom.appendChild(logDom);
};
var consoleWithCSS = function (console, args, css) {
    var messages = args.reduce(function (acc, cur) {
        if (typeof cur === 'object' || Array.isArray(cur)) {
            return "".concat(acc, " ").concat(JSON.stringify(cur));
        }
        return "".concat(acc, " ").concat(cur);
    }, '');
    console.log("%c".concat(messages), css);
};
var LOG_LEVEL;
(function (LOG_LEVEL) {
    LOG_LEVEL[LOG_LEVEL["TRACE"] = 0] = "TRACE";
    LOG_LEVEL[LOG_LEVEL["DEBUG"] = 1] = "DEBUG";
    LOG_LEVEL[LOG_LEVEL["INFO"] = 2] = "INFO";
    LOG_LEVEL[LOG_LEVEL["WARN"] = 3] = "WARN";
    LOG_LEVEL[LOG_LEVEL["ERROR"] = 4] = "ERROR";
    LOG_LEVEL[LOG_LEVEL["FATAL"] = 5] = "FATAL";
})(LOG_LEVEL || (LOG_LEVEL = {}));
/**
 * TvingLogModule은 Sentry, GA360, Console 등 다양한 로그를 관리하는 모듈입니다.
 *
 */
var TvingLogModule = /** @class */ (function () {
    function TvingLogModule(option) {
        this.isDebug = process.env.NODE_ENV === 'development';
        this.isReal = process.env.REACT_APP_RELEASE === 'PRODUCTION';
        this.consoleDom = null;
        this.logLevel = LOG_LEVEL.DEBUG;
        // GA 전송 내역을 콘솔에 출력할지 여부
        this.isGaConsole = false;
        // 임의의 DOM에 콘솔 내역을 출력
        this.isConsole = false;
        this.isGaSentry = true;
        this.isAmplitudeConsole = false;
        this.lastPageView = '';
        this.console = console;
        this.sentry = Sentry;
        this.isConsole = !this.isReal && !!(option === null || option === void 0 ? void 0 : option.isConsole);
        this.isGaConsole = !this.isReal && !!(option === null || option === void 0 ? void 0 : option.isGaConsole);
        if (!this.isReal) {
            this.registerKeydownEvent();
        }
        if (this.isConsole) {
            this.drawConsoleDom();
        }
    }
    TvingLogModule.prototype.registerKeydownEvent = function () {
        var _this = this;
        document.addEventListener('keydown', function (e) {
            // noinspection JSDeprecatedSymbols
            switch (e.keyCode) {
                case 48: {
                    if (_this.consoleDom) {
                        _this.consoleDom.innerHTML = '';
                    }
                    break;
                }
                case 49: {
                    _this.isConsole = !_this.isConsole;
                    if (_this.isConsole) {
                        _this.drawConsoleDom();
                    }
                    else if (_this.consoleDom) {
                        document.body.removeChild(_this.consoleDom);
                    }
                    break;
                }
                default:
                    break;
            }
        });
    };
    TvingLogModule.prototype.drawConsoleDom = function () {
        var consoleDom = document.getElementById('tving-console');
        if (consoleDom) {
            this.consoleDom = consoleDom;
        }
        else {
            this.consoleDom = document.createElement('div');
            this.consoleDom.setAttribute('id', 'tving-console');
            document.body.appendChild(this.consoleDom);
        }
    };
    TvingLogModule.prototype.log = function (style, logLevel) {
        var args = [];
        for (var _i = 2; _i < arguments.length; _i++) {
            args[_i - 2] = arguments[_i];
        }
        if (this.logLevel > logLevel) {
            return;
        }
        if (this.isConsole && this.consoleDom) {
            logDomWithCSS(this.consoleDom, args, style);
        }
        if (this.isDebug) {
            consoleWithCSS(this.console, args, style);
            this.console.groupCollapsed('Call stack');
            this.console.trace(); // hidden in collapsed group
            this.console.groupEnd();
        }
        else {
            this.sentry.addBreadcrumb({
                category: 'log',
                message: JSON.stringify(args),
                level: 'debug',
            });
        }
    };
    TvingLogModule.prototype.trace = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (args) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.log.apply(this, __spreadArray(['background: skyblue; color: black', LOG_LEVEL.TRACE], __read(args), false));
        }
    };
    TvingLogModule.prototype.traceSentry = function (message, extra, tags) {
        this.sentry.captureMessage(message, {
            level: 'log',
            extra: extra,
            tags: tags,
        });
    };
    TvingLogModule.prototype.debug = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (args) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.log.apply(this, __spreadArray(['background: green; color: yellow', LOG_LEVEL.DEBUG], __read(args), false));
        }
    };
    TvingLogModule.prototype.debugSentry = function (message, extra, tags) {
        this.sentry.captureMessage(message, {
            level: 'debug',
            extra: extra,
            tags: tags,
        });
    };
    TvingLogModule.prototype.info = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (args) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.log.apply(this, __spreadArray(['background: black; color: white', LOG_LEVEL.INFO], __read(args), false));
        }
    };
    TvingLogModule.prototype.infoSentry = function (message, extra, tags) {
        this.sentry.captureMessage(message, {
            level: 'info',
            extra: extra,
            tags: tags,
        });
    };
    TvingLogModule.prototype.warn = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (args) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.log.apply(this, __spreadArray(['background: orange; color: red', LOG_LEVEL.WARN], __read(args), false));
        }
    };
    TvingLogModule.prototype.warnSentry = function (message, extra, tags) {
        this.sentry.captureMessage(message, {
            level: 'warning',
            extra: extra,
            tags: tags,
        });
    };
    TvingLogModule.prototype.error = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (args) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.log.apply(this, __spreadArray(['background: darkred; color: yellow', LOG_LEVEL.ERROR], __read(args), false));
        }
    };
    TvingLogModule.prototype.errorSentry = function (message, extra, tags) {
        this.sentry.captureMessage(message, {
            level: 'error',
            extra: extra,
        });
    };
    TvingLogModule.prototype.gaPageView = function (pageName) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        ReactGA.pageview(window.location.pathname, undefined, pageName);
        this.lastPageView = pageName;
        if (this.isGaConsole) {
            this.console.log("GA360 PageView: ".concat(pageName));
        }
        if (this.isGaSentry) {
            this.sentry.addBreadcrumb({
                category: 'ga',
                type: 'pageview',
                message: "GA360 PageView: ".concat(pageName),
                level: 'info',
            });
        }
    };
    TvingLogModule.prototype.getLastPageView = function () {
        return this.lastPageView;
    };
    TvingLogModule.prototype.gaEvent = function (category, action, label) {
        ReactGA.event({
            category: category,
            action: action,
            label: label,
        });
        if (this.isGaConsole) {
            this.console.log("GA360 Event: ".concat(category, ", ").concat(action, ", ").concat(label));
        }
        if (this.isGaSentry) {
            this.sentry.addBreadcrumb({
                category: 'ga',
                type: 'event',
                message: "GA360 Event: ".concat(category, ", ").concat(action, ", ").concat(label),
                level: 'info',
            });
        }
    };
    TvingLogModule.prototype.amplitudeEvent = function (_a) {
        var event = _a.event, eventProperties = _a.eventProperties;
        amplitudeEvent({
            event: event,
            eventProperties: eventProperties,
        });
        if (this.isAmplitudeConsole) {
            this.console.log('amplitudeEvent');
        }
    };
    return TvingLogModule;
}());
export default new TvingLogModule();
